/**
 * Tool call contracts for the conversation loop.
 *
 * `ToolEnvelope` wraps every call the loop dispatches — any tool the turn was given, executed by the tool registry —
 * with idempotency and correlation metadata, so a retry is safe even for a tool that changes the organization.
 * `ToolResultEnvelope` is what comes back: the output, or an error whose class the model reads.
 *
 * `hire-employee` keeps its contract here: its definition (`tools/definitions/orchestrator.ts`) is generated from it
 * and its executor parses with it. The other eight tools this file once enumerated are catalog tools with their own
 * definitions (`find-entity-by-description`, `save_artifact`) or retired.
 */
import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// Envelope — wraps every tool dispatch
// ─────────────────────────────────────────────────────────────────────────────

export const ToolEnvelopeSchema = z.object({
  /** Any tool the turn was given — a catalog tool, an entity-skill tool, `load_skill`. Open on purpose. */
  toolName: z.string().min(1).max(200),
  /**
   * Idempotency key — `${conversationMessageId}:${toolCallId}`.
   * Critical for destructive tools so a mid-loop retry doesn't double-execute.
   */
  idempotencyKey: z.string().min(1).max(200),
  /** Correlation: which conversation + assistant turn produced this call. */
  conversationId: z.number().int().positive(),
  messageId: z.number().int().positive(),
  organizationId: z.number().int().positive(),
  /**
   * Modules enabled for `organizationId` at the time the turn was assembled.
   *
   * Carried alongside the org id so a tool that filters by module cannot be handed one without
   * the other — the omission that let the engine's marketplace and agent-template readers return
   * rows from modules the org does not have (QG-ENG-SVC-011). Build an `OrganizationContext` from
   * the pair with `organizationContextFromWire`; do not read this array directly.
   */
  enabledModules: z.array(z.string()).default([]),
  userId: z.number().int().positive().optional(),
  /**
   * Workstream-scoped permissions granted when the turn/context was assembled.
   * These travel with every tool call in the workstream so tools do not prompt
   * or re-resolve permission on each execution.
   */
  permissions: z.array(z.string().min(1).max(160)).optional(),
  permissionContextId: z.string().min(1).max(200).optional(),
  /** Anthropic tool_use_id from Sonnet's response — opaque to us. */
  toolUseId: z.string().min(1).max(200),
});

export type ToolEnvelope = z.infer<typeof ToolEnvelopeSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// hire-employee — creates an employee, switched off, from a persona the organization holds
// ─────────────────────────────────────────────────────────────────────────────

export const HireEmployeeInputSchema = z.object({
  templateId: z.number().int().positive(),
  /** Optional name override; defaults to template's display name. */
  daemonName: z.string().min(1).max(200).optional(),
  projectId: z.number().int().positive().optional(),
  /** The runtime policy the employee runs under (owner 2026-10-14); absent → the workforce surface's default. */
  runtimePolicyId: z.number().int().positive().optional(),
});

export const HireEmployeeOutputSchema = z.object({
  daemonId: z.number().int().positive(),
  daemonName: z.string(),
  templateId: z.number().int().positive(),
  panelLink: z.string(),
});

export type HireEmployeeInput = z.infer<typeof HireEmployeeInputSchema>;
export type HireEmployeeOutput = z.infer<typeof HireEmployeeOutputSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Tool-failure taxonomy (returned to model when a tool errors)
// ─────────────────────────────────────────────────────────────────────────────

export const ToolErrorClassSchema = z.enum([
  /** Network/timeout/transient — orchestrator may retry once. */
  'transient',
  /** Bad input shape — model should self-correct. */
  'validation',
  /** Auth/permission denied — model should not retry. */
  'permission',
  /** Module disabled — model should adapt or give up gracefully. */
  'module_disabled',
  /** Destructive partial failure — turn must be marked failed. */
  'destructive_partial',
  /** Unknown — surface as opaque failure. */
  'unknown',
]);

export type ToolErrorClass = z.infer<typeof ToolErrorClassSchema>;

export const ToolErrorSchema = z.object({
  errorClass: ToolErrorClassSchema,
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
});

export type ToolError = z.infer<typeof ToolErrorSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Tool result envelope (success or error)
// ─────────────────────────────────────────────────────────────────────────────

export const ToolResultEnvelopeSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ok'),
    envelope: ToolEnvelopeSchema,
    output: z.unknown(),
    durationMs: z.number().int().min(0),
  }),
  z.object({
    status: z.literal('error'),
    envelope: ToolEnvelopeSchema,
    error: ToolErrorSchema,
    durationMs: z.number().int().min(0),
  }),
]);

export type ToolResultEnvelope = z.infer<typeof ToolResultEnvelopeSchema>;
